import {
  Event,
  Id,
  Identifiable,
  List,
  Milestone,
  ProjectElementType,
  RankedElement,
  Task,
} from "@planzone/dto";
import { Boundaries } from "@planzone/core";

type Range = {
  min: RankedElement<any> | null;
  max: RankedElement<any> | null;
};

function lookForList(
  list: Identifiable<List>,
  listId: Id,
): Identifiable<List> | null {
  return list.uuid === listId
    ? list
    : list.subLists.reduce<Identifiable<List> | null>(
        (foundList, subList) => foundList || lookForList(subList, listId),
        null,
      );
}

function lookForTask(
  list: Identifiable<List>,
  taskId: Id,
): Identifiable<Task> | null {
  const task = list.tasks.find(({ uuid }) => uuid === taskId);

  return task
    ? task
    : list.subLists.reduce<Identifiable<Task> | null>(
        (foundTask, subList) => foundTask || lookForTask(subList, taskId),
        null,
      );
}

function lookForMilestone(
  list: Identifiable<List>,
  milestoneId: Id,
): Identifiable<Milestone> | null {
  const milestone = list.milestones.find(({ uuid }) => uuid === milestoneId);

  return milestone
    ? milestone
    : list.subLists.reduce<Identifiable<Milestone> | null>(
        (foundMilestone, subList) =>
          foundMilestone || lookForMilestone(subList, milestoneId),
        null,
      );
}

function lookForEvent(
  list: Identifiable<List>,
  eventId: Id,
): Identifiable<Event> | null {
  const event = list.events.find(({ uuid }) => uuid === eventId);

  return event
    ? event
    : list.subLists.reduce<Identifiable<Event> | null>(
        (foundEvent, subList) => foundEvent || lookForEvent(subList, eventId),
        null,
      );
}

function getElement(
  list: Identifiable<List>,
  projectElementId: Id,
  projectElementType: ProjectElementType,
):
  | Identifiable<List>
  | Identifiable<Task>
  | Identifiable<Milestone>
  | Identifiable<Event>
  | null {
  return projectElementType === ProjectElementType.LIST
    ? lookForList(list, projectElementId)
    : projectElementType === ProjectElementType.TASK
      ? lookForTask(list, projectElementId)
      : projectElementType === ProjectElementType.MILESTONE
        ? lookForMilestone(list, projectElementId)
        : projectElementType === ProjectElementType.EVENT
          ? lookForEvent(list, projectElementId)
          : null;
}

function removeElement(
  list: Identifiable<List>,
  projectElementId: Id,
): Identifiable<List> {
  return {
    ...list,
    milestones: list.milestones.filter(({ uuid }) => uuid !== projectElementId),
    tasks: list.tasks.filter(({ uuid }) => uuid !== projectElementId),
    events: list.events.filter(({ uuid }) => uuid !== projectElementId),
    subLists: list.subLists
      .filter(({ uuid }) => uuid !== projectElementId)
      .map((subList) => removeElement(subList, projectElementId)),
  };
}

function placeElement(
  list: Identifiable<List>,
  element:
    | Identifiable<List>
    | Identifiable<Task>
    | Identifiable<Milestone>
    | Identifiable<Event>,
  parentList: Identifiable<List>,
  projectElementType: ProjectElementType,
): Identifiable<List> {
  return {
    ...list,
    tasks:
      list.uuid === parentList.uuid &&
      projectElementType === ProjectElementType.TASK
        ? [...list.tasks, element as Identifiable<Task>]
        : list.tasks,
    milestones:
      list.uuid === parentList.uuid &&
      projectElementType === ProjectElementType.MILESTONE
        ? [...list.milestones, element as Identifiable<Milestone>]
        : list.milestones,
    events:
      list.uuid === parentList.uuid &&
      projectElementType === ProjectElementType.EVENT
        ? [...list.events, element as Identifiable<Event>]
        : list.events,
    subLists:
      list.uuid === parentList.uuid &&
      projectElementType === ProjectElementType.LIST
        ? [...list.subLists, element as Identifiable<List>]
        : list.subLists.map((subList) =>
            placeElement(subList, element, parentList, projectElementType),
          ),
  };
}

function calculateNextRank({ min, max }: Range): number {
  return min && max
    ? (min.rank + max.rank) / 2
    : min && !max
      ? min.rank + 1
      : !min && max
        ? max.rank - 1
        : 0;
}

function handleMoveListElementRequest(
  list: Identifiable<List>,
  {
    projectElementType,
    projectElementId,
    nextParentList,
    range,
  }: Boundaries["moveListElement"]["request"],
): Identifiable<List> {
  const element = getElement(list, projectElementId, projectElementType);

  return element
    ? placeElement(
        removeElement(list, projectElementId),
        projectElementType === ProjectElementType.MILESTONE ||
          projectElementType === ProjectElementType.EVENT
          ? { ...element, keyRank: calculateNextRank(range) }
          : {
              ...element,
              activityKeyRank: calculateNextRank(range),
            },
        nextParentList,
        projectElementType,
      )
    : list;
}

function handleMoveListElementSuccess(
  list: Identifiable<List>,
  {
    projectElementType,
    projectElementId,
    nextParentList,
  }: Boundaries["moveListElement"]["success"],
): Identifiable<List> {
  const element = getElement(list, projectElementId, projectElementType);

  return element
    ? placeElement(
        removeElement(list, projectElementId),
        element,
        nextParentList,
        projectElementType,
      )
    : list;
}

function handleMoveListElementFailure(
  list: Identifiable<List>,
  {
    projectElementType,
    projectElementId,
    nextParentList,
  }: Boundaries["moveListElement"]["failure"],
): Identifiable<List> {
  const element = getElement(list, projectElementId, projectElementType);

  return element
    ? placeElement(
        removeElement(list, projectElementId),
        element,
        nextParentList,
        projectElementType,
      )
    : list;
}

export {
  handleMoveListElementRequest,
  handleMoveListElementSuccess,
  handleMoveListElementFailure,
};
